const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface Lead {
  id: string;
  customerName: string;
  vehicle: string;
  vehicleYear: string;
  scenario: string;
  stage: 'unassigned' | 'assigned' | 'new' | 'contacted' | 'appointment' | 'no-interest';
  originalStatus?: string; // Backend status for filtering
  assignedTo?: string;
  assignedAt?: string;
  lastContact: string;
  channel: 'whatsapp' | 'sms' | 'phone';
  interestLevel: 'high' | 'medium' | 'low';
  priority?: 'high' | 'medium' | 'low';
  automationActive?: boolean;
  aiSummary: string;
  notes: string;
  phone: string;
  updatedAt?: string;
  chatHistory: Array<{
    id: string;
    sender: 'bot' | 'customer';
    message: string;
    timestamp: string;
  }>;
  handoffReason: string;
}

class LeadService {
  async getLeads(): Promise<Lead[]> {
    try {
      // Use the full endpoint to get all necessary fields including assignedTo
      const response = await fetch(`${API_BASE_URL}/v1/leads/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const leads = await response.json();

      // The full endpoint already returns the correct format, just return it
      return leads;
    } catch (error) {
      console.error('Error fetching leads:', error);
      return [];
    }
  }


  async assignLead(leadId: string, agentName: string): Promise<Lead | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/leads/${leadId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
        body: JSON.stringify({ agentName }),
      });

      if (!response.ok) {
        throw new Error('Failed to assign lead');
      }

      return await response.json();
    } catch (error) {
      console.error('Error assigning lead:', error);
      return null;
    }
  }

  async updateLeadStage(leadId: string, stage: Lead['stage']): Promise<Lead | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/leads/${leadId}/stage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
        body: JSON.stringify({ stage }),
      });

      if (!response.ok) {
        throw new Error('Failed to update lead stage');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating lead stage:', error);
      return null;
    }
  }

  async closeLead(leadId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error closing lead:', error);
      return false;
    }
  }
}

export const leadService = new LeadService();
